import React from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import useThemeColor from "../../../hooks/useThemeColor";
import MUITimePicker from "../MUITimePicker/MUITimePicker";
import ToggleSwitch from "../Switchery/switchery";
import FormInput from "../Input/formInput";
import "react-datetime/css/react-datetime.css";
import "./scheduleStyle.css";
import "../MUITimePicker/MUITimePicker.css";

const ScheduleDay = ({ schedule, dayIndex, handleScheduleChange, handleAddTimeSlot, handleDeleteTimeSlot, toggleDayChecked, handleAddBreakForSlot, handleRemoveBreakForSlot, handleBreakChange, isProfilePage = false, isDarkMode = false, isStaffSchedule = false }) => {
    const isDisabled = !schedule.checked;

    const appSettings = useSelector((state) => state.appSettings);
    const { appTheme } = appSettings;

    const themeColor = useThemeColor();

    // Add debugging
    console.log(`ScheduleDay ${dayIndex} - checked:`, schedule.checked);

    // Helper for time column class
    const timeColClass = isProfilePage ? "col-4 d-flex align-items-center" : "col-3 d-flex align-items-center";

    return (
        <div
            className="mb-4 p-3 rounded"
            style={{
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                boxShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 1px 2px rgba(0,0,0,0.1)'
            }}
        >
            {schedule.timeSlots.map((slot, slotIndex) => {
                const hasBreak = !!slot.break;
                return (
                    <>
                        <div
                            className={`row d-flex align-items-center mb-3 ${isProfilePage ? "gx-3" : ""}`}
                            key={slotIndex}
                        >
                            {/* Toggle Switch (Only for First Slot) */}
                            <div className="col-3 d-flex align-items-center">
                                {slotIndex === 0 && (
                                    <ToggleSwitch
                                        isChecked={schedule.checked}
                                        onToggle={(checked) => {
                                            console.log(`Toggle called for day ${dayIndex}, new value:`, checked);
                                            toggleDayChecked(dayIndex, checked);
                                        }}
                                        label={schedule.day}
                                        name={`day-toggle-${dayIndex}`}
                                        themeColor={themeColor}
                                    />
                                )}
                            </div>

                            {/* Start Time */}
                            <div className={timeColClass}>
                                <MUITimePicker
                                    label="Start Time"
                                    value={slot.from ? moment(slot.from, "HH:mm") : null}
                                    onChange={(time) => handleScheduleChange(dayIndex, slotIndex, "from", time)}
                                    ampm={true}
                                    disabled={isDisabled}
                                    placeholder="Select start time"
                                    clearable={true}
                                    name={`start-time-${dayIndex}-${slotIndex}`}
                                />
                            </div>

                            {/* "to" Label */}
                            <div className="col-1 d-flex align-items-center justify-content-center">
                                <span
                                    className={isProfilePage ? "fw-bold" : ""}
                                    style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}
                                >
                                    to
                                </span>
                            </div>

                            {/* End Time */}
                            <div className={timeColClass}>
                                <MUITimePicker
                                    label="End Time"
                                    value={slot.to ? moment(slot.to, "HH:mm") : null}
                                    onChange={(time) => handleScheduleChange(dayIndex, slotIndex, "to", time)}
                                    ampm={true}
                                    disabled={isDisabled}
                                    placeholder="Select end time"
                                    clearable={true}
                                    name={`end-time-${dayIndex}-${slotIndex}`}
                                />
                            </div>

                            {/* Delete Button (Not for First Slot) */}
                            <div className="col-1 d-flex align-items-center justify-content-center">
                                {slotIndex > 0 && (
                                    <button
                                        className="btn btn-sm rounded-pill px-3 py-2"
                                        style={{
                                            backgroundColor: 'transparent',
                                            color: '#ef4444',
                                            border: '1px solid #ef4444',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onClick={() => handleDeleteTimeSlot(dayIndex, slotIndex)}
                                        disabled={isDisabled}
                                        title="Remove this time slot"
                                    >
                                        <i className="fa fa-trash me-1"></i>
                                        Remove
                                    </button>
                                )}
                            </div>

                            {/* Add Button (Only for Last Slot and Not on Profile Page) */}
                            <div className="col-1 d-flex align-items-center justify-content-center">
                                {slotIndex === schedule.timeSlots.length - 1 && !isProfilePage && (
                                    <>
                                        {!isStaffSchedule && (
                                            <button
                                                className="btn btn-sm rounded-pill px-3 py-2"
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    color: themeColor,
                                                    border: `1px solid ${themeColor}`,
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onClick={() => handleAddTimeSlot(dayIndex)}
                                                disabled={isDisabled}
                                                title="Add another time slot"
                                            >
                                                <i className="fa fa-plus me-1"></i>
                                                Time Slot
                                            </button>
                                        )}
                                        {isStaffSchedule && !hasBreak && (
                                            <button
                                                className="btn btn-sm rounded-pill px-3 py-2"
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    color: themeColor,
                                                    border: `1px solid ${themeColor}`,
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onClick={() => handleAddBreakForSlot(dayIndex, slotIndex)}
                                                disabled={isDisabled}
                                                title="Add break for this slot"
                                            >
                                                <i className="fa fa-plus me-1"></i>
                                                Add Break
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Break Fields Row - Only show when hasBreak for this slot and isStaffSchedule */}
                        {isStaffSchedule && hasBreak && (
                            <>
                                <div className={`row align-items-center mb-3 ${isProfilePage ? "gx-3" : ""}`}>
                                    {/* Empty column for toggle alignment */}
                                    <div className="col-3"></div>

                                    {/* Start Break Time - Align under Start Time */}
                                    <div className={timeColClass}>
                                        <MUITimePicker
                                            label="Start Break Time"
                                            value={slot.break.startBraekTime ? moment(slot.break.startBraekTime, "HH:mm") : null}
                                            onChange={(time) => handleBreakChange(dayIndex, slotIndex, "startBraekTime", time)}
                                            ampm={true}
                                            disabled={isDisabled}
                                            placeholder="Select start break time"
                                            clearable={true}
                                            name={`start-break-time-${dayIndex}-${slotIndex}`}
                                        />
                                    </div>

                                    {/* Spacer Label (instead of "to") */}
                                    <div className="col-1 d-flex align-items-center justify-content-center">
                                        {/* <span 
                                            style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}
                                        >
                                            for
                                        </span> */}
                                    </div>

                                    {/* Break Minutes - Align under End Time */}
                                    <div className={timeColClass}>
                                        <div className="d-flex flex-column w-100">
                                            <label className="fw-bold mb-1" style={{ color: isDarkMode ? '#d1d5db' : '#6b7280' }}>
                                                Break Minutes
                                            </label>
                                            <FormInput
                                                placeHolder="Break Minutes"
                                                type="number"
                                                min="0"
                                                value={slot.break.breakMinutes || ""}
                                                onchange={(e) => handleBreakChange(dayIndex, slotIndex, "breakMinutes", e)}
                                                width="100%"
                                                className="form-control align-input"
                                                disabled={isDisabled}
                                            />
                                        </div>
                                    </div>

                                    {/* Remove Button */}
                                    <div className="col-1 d-flex align-items-center justify-content-center mt-4">
                                        <button
                                            className="btn btn-sm rounded-pill px-3 py-2"
                                            onClick={() => handleRemoveBreakForSlot(dayIndex, slotIndex)}
                                            type="button"
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: '#dc3545',
                                                border: '1px solid #dc3545',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#dc3545';
                                                e.target.style.color = '#ffffff';
                                                e.target.style.transform = 'translateY(-1px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = 'transparent';
                                                e.target.style.color = '#dc3545';
                                                e.target.style.transform = 'translateY(0)';
                                            }}
                                            disabled={isDisabled}
                                        >
                                            <i className="fa fa-trash me-1"></i>
                                            Remove
                                        </button>
                                    </div>

                                    {/* Spacer for Add Button */}
                                    <div className="col-1"></div>
                                </div>

                                {/* Break Inclusive Toggle Row */}
                                <div className={`row align-items-center mb-3 ${isProfilePage ? "gx-3" : ""}`}>
                                    <div className="col-3"></div>
                                    <div className={timeColClass}>
                                        <div className="d-flex align-items-center">
                                            <input
                                                type="checkbox"
                                                id={`break-inclusive-${dayIndex}-${slotIndex}`}
                                                checked={slot.break.isBreakInclusive || false}
                                                onChange={(e) => handleBreakChange(dayIndex, slotIndex, "isBreakInclusive", e.target.checked)}
                                                disabled={isDisabled}
                                                style={{
                                                    width: '18px',
                                                    height: '18px',
                                                    marginRight: '10px',
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                    accentColor: themeColor
                                                }}
                                            />
                                            <label
                                                htmlFor={`break-inclusive-${dayIndex}-${slotIndex}`}
                                                style={{
                                                    color: isDarkMode ? '#d1d5db' : '#6b7280',
                                                    marginBottom: 0,
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                    userSelect: 'none'
                                                }}
                                            >
                                                Break time is included in shift hours
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-1"></div>
                                    <div className={timeColClass}></div>
                                    <div className="col-2"></div>
                                </div>
                            </>
                        )}
                    </>
                );
            })}
            <hr
                className="mb-3"
                style={{ borderColor: isDarkMode ? '#4b5563' : '#e5e7eb' }}
            />
        </div>
    );
};

const ScheduleManager = ({ schedules, setSchedules, isProfilePage = false, isDarkMode = false, isStaffSchedule = false }) => {
    const handleScheduleChange = (dayIndex, slotIndex, field, value) => {
        const updatedSchedules = [...schedules];
        const formattedValue = field === "description" ? value : moment.isMoment(value) ? value.format("HH:mm") : value;
        updatedSchedules[dayIndex].timeSlots[slotIndex][field] = formattedValue;
        setSchedules(updatedSchedules);
    };

    const handleAddTimeSlot = (dayIndex) => {
        const updatedSchedules = [...schedules];
        updatedSchedules[dayIndex].timeSlots.push({ from: "", to: "", description: "", break: null });
        setSchedules(updatedSchedules);
    };

    const handleDeleteTimeSlot = (dayIndex, slotIndex) => {
        const updatedSchedules = [...schedules];
        if (updatedSchedules[dayIndex].timeSlots.length > 1) {
            updatedSchedules[dayIndex].timeSlots.splice(slotIndex, 1);
            setSchedules(updatedSchedules);
        }
    };

    const handleAddBreakForSlot = (dayIndex, slotIndex) => {
        const updatedSchedules = [...schedules];
        if (!updatedSchedules[dayIndex].timeSlots[slotIndex].break) {
            updatedSchedules[dayIndex].timeSlots[slotIndex].break = { startBraekTime: "", breakMinutes: null, isBreakInclusive: false };
        }
        setSchedules(updatedSchedules);
    };

    const handleRemoveBreakForSlot = (dayIndex, slotIndex) => {
        const updatedSchedules = [...schedules];
        updatedSchedules[dayIndex].timeSlots[slotIndex].break = null;
        setSchedules(updatedSchedules);
    };

    const handleBreakChange = (dayIndex, slotIndex, field, value) => {
        const updatedSchedules = [...schedules];
        let formattedValue;
        if (field === "breakMinutes") {
            const val = value.target ? value.target.value : value;
            formattedValue = val === "" ? null : parseInt(val) || null;
        } else if (field === "isBreakInclusive") {
            formattedValue = value;
        } else {
            formattedValue = moment.isMoment(value) ? value.format("HH:mm") : value;
        }
        updatedSchedules[dayIndex].timeSlots[slotIndex].break[field] = formattedValue;
        setSchedules(updatedSchedules);
    };

    const toggleDayChecked = (dayIndex, checked) => {
        console.log(`toggleDayChecked called - dayIndex: ${dayIndex}, checked: ${checked}`);

        // Create a deep copy to ensure state update
        const updatedSchedules = schedules.map((schedule, index) => {
            if (index === dayIndex) {
                return {
                    ...schedule,
                    checked: checked,
                    timeSlots: schedule.timeSlots.map(slot => ({ ...slot, break: slot.break ? { ...slot.break } : null })) // Deep copy slots and breaks
                };
            }
            return schedule;
        });

        console.log('Updated schedules:', updatedSchedules);
        setSchedules(updatedSchedules);
    }

    return (
        <div className="pt-3 ps-3">
            {schedules.map((schedule, dayIndex) => (
                <ScheduleDay
                    key={dayIndex}
                    schedule={schedule}
                    dayIndex={dayIndex}
                    handleScheduleChange={handleScheduleChange}
                    handleAddTimeSlot={handleAddTimeSlot}
                    handleDeleteTimeSlot={handleDeleteTimeSlot}
                    toggleDayChecked={toggleDayChecked}
                    handleAddBreakForSlot={handleAddBreakForSlot}
                    handleRemoveBreakForSlot={handleRemoveBreakForSlot}
                    handleBreakChange={handleBreakChange}
                    isProfilePage={isProfilePage}
                    isDarkMode={isDarkMode}
                    isStaffSchedule={isStaffSchedule}
                />
            ))}
        </div>
    );
};

export default ScheduleManager;